import { Fragment, type ReactNode } from 'react';
import { Link } from 'react-router-dom';

export interface Crumb {
  label: string;
  to?: string;
}

/**
 * The top of every signed-in page: where you are, what this is, and the one or two things to do here.
 *
 * Actions sit on the right of the title rather than somewhere down the page, so the main thing a page is
 * for is never below the fold.
 */
export function PageHeader({
  crumbs,
  title,
  meta,
  actions,
}: {
  crumbs?: Crumb[];
  title: ReactNode;
  meta?: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <header className="page-head">
      {crumbs && crumbs.length > 0 && (
        <p className="crumb">
          {crumbs.map((c, i) => (
            <Fragment key={i}>
              {i > 0 && <span className="crumb-sep">›</span>}
              {c.to ? <Link to={c.to}>{c.label}</Link> : <span>{c.label}</span>}
            </Fragment>
          ))}
        </p>
      )}
      <div className="page-head-row">
        <div className="page-head-text">
          <h1>{title}</h1>
          {meta && <div className="page-meta">{meta}</div>}
        </div>
        {actions && <div className="page-actions">{actions}</div>}
      </div>
    </header>
  );
}

/**
 * The heading for one section of a workspace or org: the menu item you chose, and what it is for.
 * Sits under the PageHeader, which names the workspace or org itself.
 */
export function SectionHeader({ title, blurb, actions }: { title: string; blurb?: string; actions?: ReactNode }) {
  return (
    <div className="section-head">
      <div>
        <h2>{title}</h2>
        {blurb && <p className="section-blurb">{blurb}</p>}
      </div>
      {actions && <div className="page-actions">{actions}</div>}
    </div>
  );
}
